export default {
  brand: 'USFansSheet.help',
  language: 'Language',

  'meta.title': 'USFans Spreadsheet 2026 — Finds, QC, Reviews & Buying Guides',
  'meta.desc': 'Explore the USFans Spreadsheet with product finds, QC guides, reviews, shipping costs and safety information. Updated for 2026.',
  'meta.articleTitle': 'What Is the USFans Spreadsheet? Complete 2026 Guide',
  'meta.articleDesc': 'Learn what the USFans Spreadsheet is, how to use it, its product categories, QC, shipping and safety tips in this beginner-friendly guide.',

  'nav.home': 'Home',
  'nav.shop': 'Shopping Guide',
  'nav.spreadsheet': 'Spreadsheet',
  'nav.categories': 'Categories',
  'nav.guides': 'Guides',

  'hero.badge': 'Independent resource · Updated for 2026',
  'hero.title': 'USFans Spreadsheet — Find Better Products, QC & Buying Guides',
  'hero.subtitle': 'Explore the USFans Spreadsheet in one organized hub. Discover product finds, category guides, QC tips, shipping advice and safety resources before you buy.',
  'hero.ctaPrimary': 'USFans Spreadsheet',
  'hero.ctaSecondary': 'Buy It Now',
  'hero.trust': 'Independent · Safety-focused · Multilingual',

  'categories.title': 'Browse Product Categories',
  'categories.subtitle': 'Jump straight to any category on the main platform.',
  'categories.dropdownLabel': 'Choose a category',
  'categories.viewAll': 'View all categories',

  'cat.shoes': 'Shoes',
  'cat.hoodies': 'Hoodies / Sweaters',
  'cat.tshirts': 'T-Shirts',
  'cat.jackets': 'Jackets',
  'cat.pants': 'Pants / Shorts',
  'cat.headwear': 'Headwear',
  'cat.sets': 'Sets',
  'cat.underwear': 'Underwear / Underpants',
  'cat.jersey': 'Jersey',
  'cat.accessories': 'Accessories',
  'cat.bags': 'Bags',
  'cat.belts': 'Belts',
  'cat.perfumes': 'Perfumes',
  'cat.other': 'Other Stuff',

  'what.title': 'What Is the USFans Spreadsheet?',
  'what.p1': 'The USFans Spreadsheet is a structured way to organize product discoveries, links, categories and buying information in one place.',
  'what.p2': 'Instead of scrolling through endless pages, the spreadsheet helps you compare options, check QC notes and understand shipping and warehouse processes before placing an order.',
  'what.p3': 'It is designed as an informational directory — a research shortcut for anyone who wants to explore products more efficiently.',
  'what.cta': 'Learn How It Works',
  'what.finds.title': 'Product Finds',
  'what.finds.desc': 'Browse organized product discoveries across every category.',
  'what.qc.title': 'QC Photos',
  'what.qc.desc': 'Review quality check images before you confirm an order.',
  'what.shipping.title': 'Shipping',
  'what.shipping.desc': 'Understand routes, weight and consolidation costs.',
  'what.sellers.title': 'Sellers',
  'what.sellers.desc': 'Check seller details and reputation before you buy.',

  'guideCard.title': 'The Complete USFans Spreadsheet Guide',
  'guideCard.summary': 'Everything you need to know about the USFans Spreadsheet — what it is, how to use it, categories, QC, shipping and safety — in one beginner-friendly guide.',
  'guideCard.readTime': '8 min read',
  'guideCard.cta': 'Read the guide',

  'pillar.title': 'Latest Guides & Reviews',
  'pillar.subtitle': 'Fresh, in-depth USFans Spreadsheet articles — reviews, shipping, safety and more.',
  'pillar.readMore': 'Read article',

  'cat.label.review': 'Review',
  'cat.label.shipping': 'Shipping',
  'cat.label.coupon': 'Coupon',
  'cat.label.warehouse': 'Warehouse',
  'cat.label.safety': 'Safety',
  'cat.label.qc': 'QC Guide',
  'cat.label.guide': 'Guide',

  'faq.title': 'Frequently Asked Questions',
  'faq.q1': 'What is the USFans Spreadsheet?',
  'faq.a1': 'It is an informational directory that organizes product finds, links, categories and buying notes so you can research before purchasing.',
  'faq.q2': 'Is the USFans Spreadsheet safe to use?',
  'faq.a2': 'A spreadsheet is a directory of information, not a guarantee. Always verify links, sellers and details on the main platform before buying.',
  'faq.q3': 'How do shipping costs work?',
  'faq.a3': 'Shipping depends on weight, dimensions, destination and the chosen route or service level. Consolidation can also change the final price.',
  'faq.q4': 'Where can I find discount codes?',
  'faq.a4': 'Promotions are published by the platform itself. Check the official channels on the main website and always verify conditions and expiry.',
  'faq.q5': 'Is USFans legit?',
  'faq.a5': 'USFans is a real platform, but we always recommend verifying sellers, payment information and links independently before you order.',

  'footer.disclaimer': 'USFansSheet.help is an independent informational resource and is not affiliated with, endorsed by, or operated by USFans.',
  'footer.rights': 'All rights reserved.',
  'footer.about': 'About this site',

  'notFound.title': 'Page not found',
  'notFound.desc': 'The page you are looking for does not exist or has been moved.',

  // ---- Article page ----
  'article.backHome': 'Back to homepage',
  'article.notFound': 'Article not found',
  'article.updated': 'Last updated',
  'article.readTime': 'Reading time',
  'article.minRead': 'min read',
  'article.title': 'What Is the USFans Spreadsheet? Complete 2026 Guide',
  'article.quickAnswerTitle': 'Quick Answer',
  'article.quickAnswer': 'The USFans Spreadsheet is an informational directory that organizes product finds, category links, QC notes and buying guidance in one structured place. It helps you research products before you buy — but it does not sell anything itself, and it is not a guarantee that every seller or link is safe.',
  'article.tocTitle': 'On this page',

  'article.s1Title': 'What Is the USFans Spreadsheet?',
  'article.s1Body': 'The USFans Spreadsheet is a centralized way to browse product discoveries across many categories, from shoes and clothing to bags and accessories. Instead of digging through scattered pages, you can see organized links and notes in one view. It is a research tool, not a storefront — all purchases happen on the main platform.',

  'article.s2Title': 'How to Use the USFans Spreadsheet',
  'article.s2Body': 'Start by choosing a category that interests you, then review the listed items and any QC or shipping notes. Compare a few options before you click through to the main website. Taking a few minutes to research first usually leads to a smoother buying experience.',

  'article.s3Title': 'Product Categories Explained',
  'article.s3Body': 'The spreadsheet covers a wide range of categories, including shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes and other items. Each category groups related products so you can browse more efficiently.',

  'article.s4Title': 'QC and Quality Checks',
  'article.s4Body': 'QC, or quality control, is the step where products are inspected before shipping. Many buyers use QC photos to check details like stitching, sizing and color. If QC is available for an item, review those photos carefully before confirming your order.',

  'article.s5Title': 'Shipping and Warehouse Basics',
  'article.s5Body': 'Shipping cost depends on factors like weight, dimensions, destination and the chosen service level. Some orders pass through a warehouse where items can be stored, photographed and consolidated into fewer parcels — which can sometimes lower the total shipping cost.',

  'article.s6Title': 'Is USFans Safe and Legit?',
  'article.s6Body': 'USFans is a real platform used by many buyers, but no directory can guarantee that every seller, product or link is safe. Always verify links, seller reputation, payment details and product information independently before you purchase.',

  'article.s7Title': 'Beginner Tips',
  'article.s7Body': 'If you are new, start with a small order, review QC photos when available, and keep records of your order and communications. Check shipping terms and any promotion conditions carefully. Research first, then buy with confidence.',

  'article.tableTitle': 'Quick Comparison: Researching vs. Buying Directly',
  'article.tableHeaders': ['Factor', 'Research First', 'Buying Directly', 'Best For'],
  'article.tableRows': [
    ['Cost', 'Free to browse', 'Pay at checkout', 'Budget planning'],
    ['Speed', 'Slower (you review first)', 'Faster', 'Urgent orders'],
    ['QC', 'You can review photos', 'May skip checks', 'Quality-focused buyers'],
    ['Confidence', 'Higher after research', 'Depends on listing', 'Beginners'],
  ],

  'article.faqTitle': 'Frequently Asked Questions',
  'article.faq1q': 'What is the USFans Spreadsheet?',
  'article.faq1a': 'It is an organized directory of product finds, category links, QC notes and buying guidance that helps you research before you buy.',
  'article.faq2q': 'Is the USFans Spreadsheet free to use?',
  'article.faq2a': 'Browsing the directory is free. You only pay for items you decide to purchase on the main platform.',
  'article.faq3q': 'How do I find QC photos?',
  'article.faq3a': 'QC photos are usually available for items that pass through a warehouse. Check each listing on the main platform for available inspection images.',
  'article.faq4q': 'Is every link in the spreadsheet safe?',
  'article.faq4a': 'Not necessarily. The spreadsheet is informational, so always verify links, sellers and payment details independently before purchasing.',

  'article.relatedTitle': 'Related Guides',
  'article.related1': 'Back to the USFans Spreadsheet home',
  'article.related2': 'Browse all product categories',
  'article.ctaTitle': 'Ready to explore?',
  'article.ctaBody': 'Open the USFans Spreadsheet and start your product research today.',
  'article.cta': 'Explore USFans Spreadsheet',
};